import React from 'react';
import { useMemo } from 'react';

import { ScrollView } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { useAppTakeoverPdp } from 'components/app-takeover/use-app-takeover';
import { InnerLayout, LayoutContainer } from 'components/layout/styled';
import { FullWidthAutoHeightImage } from 'components/picture';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { isCombo } from 'state/unified-menu/utils';

import { useProductWizardContext } from '../../product-wizard';
import { useProductWizardFlow } from '../../product-wizard/use-product-wizard-flow';
import { ProductCta } from '../product-cta';
import { ProductDetailHeader } from '../product-detail-flow/product-detail-header';

import { ProductCustomizationDrinks } from './product-customization-drinks';
import { ProductCustomizationModifiers } from './product-customization-modifiers';
import { ProductCustomizationSides } from './product-customization-sides';
import { ProductCustomizationPanel } from './styled';
import { ComboSlotType } from './types';
import { getComboSlotType } from './utils';

export const ProductCustomization = () => {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobileBreakpoint();

  const { indexedMenu } = useUnifiedMenuContext();
  const { currentFlowStep, id } = useProductWizardFlow();
  const { backgroundColor: takeoverBg, backgroundImage: takeoverBgImage } = useAppTakeoverPdp(id);
  const { menuObject, selectedProduct } = useProductWizardContext();

  const comboSlotType: ComboSlotType = useMemo(
    () => getComboSlotType(indexedMenu, currentFlowStep.slotKey!),
    [indexedMenu, currentFlowStep]
  );

  const getHeaderTitle = () => {
    let title = formatMessage({ id: 'haveItYourWay' });
    const productName = selectedProduct.name?.locale ?? '';

    if (isCombo(menuObject) && productName) {
      title = productName;
    } else if (comboSlotType === ComboSlotType.SIDES) {
      title = formatMessage({ id: 'chooseSide' });
    } else if (comboSlotType === ComboSlotType.DRINKS) {
      title = formatMessage({ id: 'chooseDrink' });
    }

    return title;
  };

  // SIDES & DRINKS have an inline ProductCustomizationModifiers
  const shouldRenderModifiers = useMemo(
    () => ![ComboSlotType.SIDES, ComboSlotType.DRINKS].includes(comboSlotType),
    [comboSlotType]
  );

  return (
    <ProductCustomizationPanel>
      <ProductDetailHeader title={getHeaderTitle()} />
      <ScrollView showsVerticalScrollIndicator={!isMobile} backgroundColor={takeoverBg}>
        {takeoverBgImage && (
          <FullWidthAutoHeightImage source={takeoverBgImage} style={{ position: 'absolute' }} />
        )}
        <LayoutContainer isFullContainer={isMobile}>
          <InnerLayout>
            {comboSlotType === ComboSlotType.DRINKS ? (
              <ProductCustomizationDrinks />
            ) : (
              <ProductCustomizationSides shouldRenderModifiers={!shouldRenderModifiers} />
            )}
            {shouldRenderModifiers && <ProductCustomizationModifiers />}
          </InnerLayout>
        </LayoutContainer>
      </ScrollView>
      <ProductCta />
    </ProductCustomizationPanel>
  );
};
